import React, { createContext, useContext, useState, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';

// Create the cart context
const CartContext = createContext(null);

// Package definitions and pricing
const PACKAGES = [
  { id: 'basic', name: 'Basic', price: 30, imageCount: 1, description: '1 digital image', requires_shipping: false },
  { id: 'premium', name: 'Premium', price: 50, imageCount: 2, description: '2 digital images', requires_shipping: false },
  { id: 'ultimate', name: 'Ultimate', price: 65, imageCount: 3, description: '3 digital images', requires_shipping: false },
  { id: 'frame', name: 'Frame Package', price: 100, imageCount: 3, description: '3 digital images + 11" x 17" framed print', includedFrames: 1, requires_shipping: true }
];

// Create a provider component
export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [cartPackage, setCartPackage] = useState(null);
  const [cartRequiresShipping, setCartRequiresShipping] = useState(false);
  const toast = useToast();

  // Initialize cart from localStorage on mount
  useEffect(() => {
    const loadCartFromStorage = () => {
      try {
        console.log('CartContext: Loading cart from localStorage');
        const storedCart = localStorage.getItem('playmaker_cart');
        
        if (!storedCart) {
          console.log('CartContext: No cart data in localStorage');
          return;
        }
        
        const parsedCart = JSON.parse(storedCart);
        
        // Validate cart data structure
        if (!parsedCart || typeof parsedCart !== 'object') {
          console.error('CartContext: Invalid cart data format');
          return;
        }
        
        // Ensure the subtotal stored matches the package price (which may have been
        // fixed in newer builds). If they differ, trust the package price.
        if (
          parsedCart.package &&
          typeof parsedCart.package.price === 'number' &&
          parsedCart.total !== parsedCart.package.price
        ) {
          console.log(
            'CartContext: Correcting stored total',
            parsedCart.total,
            '→',
            parsedCart.package.price,
            'for package',
            parsedCart.package.name
          );
          parsedCart.total = parsedCart.package.price;
        }
        
        // Load items if available
        if (parsedCart.items && Array.isArray(parsedCart.items)) {
          console.log(`CartContext: Loaded ${parsedCart.items.length} items from localStorage`);
          setCartItems(parsedCart.items);
          
          // If we have package info, set it directly rather than recalculating
          if (parsedCart.package) {
            setCartPackage(parsedCart.package);
          }
          
          // If we have total info, set it directly
          if (parsedCart.total) {
            setCartTotal(parsedCart.total);
          }
          
          // Set shipping requirement if available
          if (typeof parsedCart.requires_shipping === 'boolean') {
            setCartRequiresShipping(parsedCart.requires_shipping);
          }
        }
      } catch (error) {
        console.error('CartContext: Error loading cart from localStorage:', error);
        // Reset cart on error
        localStorage.removeItem('playmaker_cart');
      }
    };
    
    loadCartFromStorage();
  }, []);

  // Update localStorage whenever cart changes
  useEffect(() => {
    const saveCartToStorage = () => {
      try {
        // Always derive the appropriate package from the current item count
        const currentPackage = getDynamicPackage(cartItems.length);
        
        // Calculate if items need shipping
        const needsShipping = 
          currentPackage?.requires_shipping || 
          currentPackage?.id === 'frame' || 
          (currentPackage?.includedFrames && currentPackage.includedFrames > 0);
        
        // Only update state if it has changed
        if (cartPackage !== currentPackage) {
          setCartPackage(currentPackage);
        }
        
        // Update total price if it has changed
        const calculatedTotal = currentPackage ? currentPackage.price : 0;
        if (cartTotal !== calculatedTotal) {
          setCartTotal(calculatedTotal);
        }
        
        // Update shipping requirement if it has changed
        if (cartRequiresShipping !== needsShipping) {
          setCartRequiresShipping(needsShipping);
        }
        
        // Save cart to localStorage
        if (cartItems.length > 0) {
          // Extract event_id from cart items
          const eventId = cartItems.length > 0 ? cartItems[0].event_id : null;
          
          const cartData = {
            items: cartItems,
            total: calculatedTotal,
            package: currentPackage,
            requires_shipping: needsShipping,
            event_id: eventId,
            image_count: cartItems.length,
            timestamp: Date.now()
          };
          
          localStorage.setItem('playmaker_cart', JSON.stringify(cartData));
          console.log('CartContext: Saved cart to localStorage', {
            itemCount: cartItems.length,
            total: calculatedTotal,
            package: currentPackage?.name
          });
        } else {
          localStorage.removeItem('playmaker_cart');
          console.log('CartContext: Removed empty cart from localStorage');
        }
      } catch (error) {
        console.error('CartContext: Error saving cart to localStorage:', error);
      }
    };
    
    saveCartToStorage();
  }, [cartItems, cartPackage, cartTotal, cartRequiresShipping]);

  // Determine the correct package based on number of items
  const getDynamicPackage = (itemCount) => {
    if (itemCount === 0) return null;
    if (itemCount === 1) return PACKAGES[0]; // Basic
    if (itemCount === 2) return PACKAGES[1]; // Premium
    if (itemCount === 3) return PACKAGES[2]; // Ultimate
    return { ...PACKAGES[2], price: 65 + ((itemCount - 3) * 20) }; // Custom pricing for more than 3
  };

  // Add an item to the cart
  const addToCart = (item) => {
    // Check if item is already in cart
    if (cartItems.some(cartItem => cartItem.id === item.id)) {
      toast({
        title: "Already in cart",
        description: "This item is already in your cart.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Add the item
    setCartItems(prevItems => [...prevItems, { ...item, qty: 1 }]);
    
    toast({
      title: "Added to cart",
      description: "Item added to your cart.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    
    return true;
  };

  // Remove an item from the cart
  const removeFromCart = (id) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== id));
    
    toast({
      title: "Removed from cart",
      description: "Item removed from your cart.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  // Clear all items from the cart
  const emptyCart = () => {
    setCartItems([]);
    localStorage.removeItem('playmaker_cart');
  };

  // Save cart data for order completion
  const saveCartForCheckout = (additionalData = {}) => {
    if (!cartItems.length) {
      console.error('CartContext: Cannot save cart for checkout: empty cart');
      return null;
    }
    
    try {
      // Format cart items with all necessary information
      const formattedItems = cartItems.map(item => ({
        id: item.id,
        name: item.template_name || item.name || 'Custom Design',
        url: item.display_url || item.export_url || item.url,
        originalUrl: item.file_url || item.export_url,
        watermarkedUrl: item.file_url_wm || item.export_url_wm || item.url,
        templateId: item.template_id || item.id,
        event_id: item.event_id, // Include event_id from the item
        price: item.price || (cartTotal / cartItems.length) // Add price for each item
      }));
      
      // Extract event_id from cart items (all should be from the same event)
      const eventId = cartItems.length > 0 ? cartItems[0].event_id : null;
      
      // Ensure we have a package, use basic if none exists
      const finalPackage = cartPackage || getDynamicPackage(cartItems.length) || PACKAGES[0];
      
      const cartData = {
        items: formattedItems,
        total: finalPackage ? finalPackage.price : 0,
        package: finalPackage,
        requires_shipping: cartRequiresShipping || 
                           finalPackage?.requires_shipping || 
                           finalPackage?.id === 'frame',
        event_id: eventId,
        image_count: cartItems.length,
        timestamp: Date.now(),
        ...additionalData
      };
      
      // Save to localStorage for checkout page
      localStorage.setItem('playmaker_cart', JSON.stringify(cartData));
      
      console.log('CartContext: Cart saved for checkout:', {
        items: formattedItems.length,
        total: cartData.total,
        package: cartData.package?.name
      });
      
      return cartData;
    } catch (error) {
      console.error('CartContext: Error saving cart for checkout:', error);
      return null;
    }
  };
  
  // Prepare and navigate to checkout
  const goToCheckout = (navigate) => {
    console.log('CartContext: goToCheckout called with cart items:', cartItems.length);
    
    if (!cartItems.length) {
      console.error('CartContext: Cannot checkout - No items in cart');
      toast({
        title: "Cannot checkout",
        description: "Please add designs to your cart first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    
    if (!cartPackage) {
      console.error('CartContext: Cannot checkout - No package selected');
      toast({
        title: "Cannot checkout",
        description: "No package was selected for your cart items.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    
    try {
      console.log('CartContext: Saving cart data for checkout', { 
        items: cartItems.length,
        package: cartPackage.name,
        total: cartTotal
      });
      
      // Save cart data
      const savedData = saveCartForCheckout();
      
      if (!savedData) {
        console.error('CartContext: saveCartForCheckout returned null or undefined');
        toast({
          title: "Checkout Error",
          description: "There was a problem preparing your checkout. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return false;
      }
      
      console.log('CartContext: Successfully saved cart data, navigating to checkout');
      
      // Navigate to checkout page
      navigate('/checkout');
      return true;
    } catch (error) {
      console.error('CartContext: Error in goToCheckout:', error);
      toast({
        title: "Checkout Error",
        description: "There was a problem preparing your checkout. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  };

  // Value object to be provided to consumers
  const value = {
    cartItems,
    cartTotal,
    cartPackage,
    cartRequiresShipping,
    addToCart,
    removeFromCart,
    emptyCart,
    saveCartForCheckout,
    goToCheckout
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook for using the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === null) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext; 